import { useMemo, useRef, useState } from "react";
import { motion, useTransform } from "framer-motion";
import { ChevronDown, Flame, MessageCircle } from "lucide-react";
import { whatsappUrl, VENUE_NAME, CEREMONY_DATE_LABEL } from "../config";
import { useLang } from "../store/lang";

const PER = 100;

export const SceneFrame = ({ progress, index, last = false, children, className = "" }) => {
  const inStart = index * PER - 8;
  const inEnd = index === 0 ? -2 : index * PER + 10;
  const outStart = index * PER + 84;
  const outEnd = last ? index * PER + 500 : index * PER + 97;
  const o = useTransform(progress, [inStart, inEnd, outStart, outEnd], [0, 1, 1, 0]);
  const y = useTransform(progress, [inStart, outEnd], [50, -50]);
  const pe = useTransform(progress, (v) => (v >= inStart - 1 && v <= (last ? outEnd : index * PER + PER + 20) ? "auto" : "none"));
  return (
    <motion.section
      style={{ opacity: o, y, pointerEvents: pe }}
      className={`absolute inset-0 flex flex-col items-center justify-center ${className}`}
    >
      {children}
    </motion.section>
  );
};

export const SceneMoon = ({ progress }) => {
  const { ur } = useLang();
  const moonScale = useTransform(progress, [0, PER], [0.85, 1.3]);
  const textY = useTransform(progress, [0, PER], [0, -90]);
  return (
    <SceneFrame progress={progress} index={0} data-testid="scene-moon">
      <motion.div
        style={{ scale: moonScale }}
        className="absolute top-[10%] right-[10%] sm:right-[16%] w-32 h-32 sm:w-48 sm:h-48 rounded-full bg-[radial-gradient(circle_at_35%_35%,#fffbe8,#f3e5ab_45%,#c9a84c_80%,#8a6d2a)] shadow-[0_0_90px_36px_rgba(243,229,171,0.28)]"
      />
      <motion.div style={{ y: textY }} className="relative text-center px-6 max-w-3xl">
        <p className="eyebrow mb-6">{ur ? "ایک زمانے کی بات ہے" : "once upon a time"}</p>
        <h2
          className={`font-royal text-4xl sm:text-6xl gold-text leading-tight ${ur ? "font-urdu leading-[1.9]" : ""}`}
          dir={ur ? "rtl" : "ltr"}
        >
          {ur ? "ایک ستارہ پیدا ہوا" : "A Star Was Born"}
        </h2>
        <p className={`mt-6 text-slate-300 font-light text-base sm:text-lg ${ur ? "font-urdu" : ""}`} dir={ur ? "rtl" : "ltr"}>
          {ur ? "اور رات کا آسمان اس کا نام دہرا رہا ہے…" : "and the night sky has been rehearsing her name ever since…"}
        </p>
        <div className="mt-16 flex flex-col items-center gap-2 text-amber-100/50">
          <span className="text-[0.62rem] tracking-[0.4em] uppercase">{ur ? "نیچے سفر کریں" : "scroll to descend"}</span>
          <motion.span animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2 }}>
            <ChevronDown size={18} />
          </motion.span>
        </div>
      </motion.div>
    </SceneFrame>
  );
};

export const SceneGarden = ({ progress }) => {
  const { ur } = useLang();
  const rise = useTransform(progress, [PER, PER * 2], [60, 0]);
  const lanterns = useMemo(
    () => Array.from({ length: 7 }, (_, i) => ({ x: 10 + ((i * 43) % 80), d: 8.5 + (i % 3) * 2.5, delay: i * 1.3 })),
    []
  );
  return (
    <SceneFrame progress={progress} index={1}>
      <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
        {lanterns.map((l) => (
          <div
            key={l.x}
            className="lantern-loop absolute bottom-[-6%]"
            style={{ left: `${l.x}%`, animationDuration: `${l.d}s`, animationDelay: `${l.delay}s` }}
          >
            <div
              className="w-7 h-10 rounded-t-full rounded-b-lg"
              style={{
                background: "radial-gradient(circle at 50% 70%, #f3e5ab, rgba(243,229,171,0.5) 60%, transparent)",
                boxShadow: "0 0 24px 8px rgba(243,229,171,0.5)",
              }}
            />
          </div>
        ))}
        <div className="cloud-drift absolute top-[16%] left-[-8%] w-[380px] h-24 bg-lavender-200/10 blur-3xl rounded-full" />
        <div className="cloud-drift-slow absolute top-[30%] right-[-10%] w-[460px] h-24 bg-rose-200/10 blur-3xl rounded-full" />
      </div>
      <motion.div style={{ y: rise }} className="relative text-center px-6 max-w-3xl">
        <p className="eyebrow mb-4">{ur ? "چاندنی کی بادشاہی میں" : "in a kingdom of moonlight"}</p>
        <h2
          className={`font-royal text-3xl sm:text-5xl gold-text leading-tight ${ur ? "font-urdu leading-[1.9]" : ""}`}
          dir={ur ? "rtl" : "ltr"}
        >
          {ur ? "باغ نے سانس روک لی" : "The Garden Held Its Breath"}
        </h2>
        <p className={`mt-5 text-slate-300 font-light ${ur ? "font-urdu" : ""}`} dir={ur ? "rtl" : "ltr"}>
          {ur ? "ہر چراغ اس کا نام سننے کے لیے اور اوپر اٹھا" : "every lantern rose a little higher, waiting to hear her name"}
        </p>
      </motion.div>
    </SceneFrame>
  );
};

const Letter = ({ ch, progress, i, n }) => {
  const start = PER * 2 + (i / n) * 52;
  const end = start + 26;
  const y = useTransform(progress, [start, end], [140, 0]);
  const o = useTransform(progress, [start, end], [0, 1]);
  const r = useTransform(progress, [start, end], [40, 0]);
  return (
    <motion.span className="gold-text" style={{ y, opacity: o, rotateZ: r, display: "inline-block" }}>
      {ch === " " ? " " : ch}
    </motion.span>
  );
};

export const SceneName = ({ progress }) => {
  const { ur } = useLang();
  const meaningO = useTransform(progress, [PER * 2 + 68, PER * 2 + 92], [0, 1]);
  const letters = "HAYA NOOR JAMAL".split("");
  return (
    <SceneFrame progress={progress} index={2}>
      <div className="text-center px-4">
        <p className="eyebrow mb-8">{ur ? "اور اس کا نام رکھا گیا" : "and she shall be called"}</p>
        <h2
          className="font-royal text-[7.5vw] sm:text-7xl leading-none whitespace-nowrap"
          style={{ perspective: 700 }}
          data-testid="journey-name"
        >
          {letters.map((ch, i) => (
            <Letter key={i} ch={ch} progress={progress} i={i} n={letters.length} />
          ))}
        </h2>
        <motion.p
          style={{ opacity: meaningO }}
          className={`mt-9 text-2xl sm:text-3xl text-rose-200/90 ${ur ? "font-urdu" : "font-script"}`}
          dir={ur ? "rtl" : "ltr"}
        >
          {ur ? "عزت · روشنی · حسن" : "grace · light · beauty"}
        </motion.p>
      </div>
    </SceneFrame>
  );
};

export const SceneLanterns = ({ progress }) => {
  const { ur } = useLang();
  const [lit, setLit] = useState(false);
  const [fill, setFill] = useState(0);
  const rafRef = useRef(null);
  const lanterns = useMemo(
    () => Array.from({ length: 9 }, (_, i) => ({ id: i, x: 8 + ((i * 37) % 84), delay: i * 0.45 })),
    []
  );

  const startHold = () => {
    cancelAnimationFrame(rafRef.current);
    let t0 = null;
    const step = (t) => {
      if (!t0) t0 = t;
      const p = Math.min(1, (t - t0) / 1100);
      setFill(p);
      if (p < 1) rafRef.current = requestAnimationFrame(step);
      else setLit(true);
    };
    rafRef.current = requestAnimationFrame(step);
  };
  const endHold = () => {
    cancelAnimationFrame(rafRef.current);
    if (!lit) setFill(0);
  };

  const CIRC = 2 * Math.PI * 34;

  return (
    <SceneFrame progress={progress} index={3}>
      {lit && (
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {lanterns.map((l) => (
            <div
              key={l.id}
              className="lantern-rise absolute bottom-[16%]"
              style={{ left: `${l.x}%`, animationDelay: `${l.delay}s` }}
            >
              <div
                className="w-8 h-11 rounded-t-full rounded-b-lg"
                style={{
                  background: "radial-gradient(circle at 50% 70%, #f3e5ab, rgba(243,229,171,0.55) 60%, transparent)",
                  boxShadow: "0 0 26px 9px rgba(243,229,171,0.55)",
                }}
              />
            </div>
          ))}
        </div>
      )}

      <div className="text-center px-6">
        <p className="eyebrow mb-4">{ur ? "روشنی کا تحفہ" : "a gift of light"}</p>
        <h2
          className={`font-royal text-3xl sm:text-5xl gold-text leading-tight ${ur ? "font-urdu leading-[1.9]" : ""}`}
          dir={ur ? "rtl" : "ltr"}
        >
          {lit ? (ur ? "آسمان ہر دعا یاد رکھتا ہے" : "The Sky Remembers Every Wish") : ur ? "اس کے لیے آسمان روشن کریں" : "Light the Sky for Her"}
        </h2>

        <div className="mt-12 flex flex-col items-center">
          <button
            data-testid="journey-lantern-hold"
            onPointerDown={startHold}
            onPointerUp={endHold}
            onPointerLeave={endHold}
            className="relative w-28 h-28 rounded-full flex items-center justify-center text-amber-200 select-none"
            style={{ touchAction: "none" }}
            aria-label="Press and hold to light the lanterns"
          >
            <svg className="absolute inset-0 w-full h-full -rotate-90" viewBox="0 0 80 80">
              <circle cx="40" cy="40" r="34" fill="none" stroke="rgba(243,229,171,0.15)" strokeWidth="2.5" />
              <circle
                cx="40"
                cy="40"
                r="34"
                fill="none"
                stroke="#D4AF37"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeDasharray={CIRC}
                strokeDashoffset={CIRC * (1 - fill)}
              />
            </svg>
            <span className={`w-20 h-20 rounded-full glass-card flex items-center justify-center ${lit ? "glow-pulse" : ""}`}>
              <Flame size={26} className={lit ? "text-amber-300" : "text-amber-200/70"} />
            </span>
          </button>
          <p className={`mt-5 text-[0.62rem] tracking-[0.35em] uppercase text-amber-100/50 ${ur ? "font-urdu tracking-normal text-sm" : ""}`} dir={ur ? "rtl" : "ltr"}>
            {lit ? (ur ? "روشنی آگے بڑھائیں — سکرول کرتے رہیں" : "carry the light forward — keep scrolling") : ur ? "دبا کر رکھیں" : "press & hold"}
          </p>
        </div>
      </div>
    </SceneFrame>
  );
};

export const SceneInvite = ({ progress }) => {
  const { ur } = useLang();
  return (
    <SceneFrame progress={progress} index={4} last>
      <div className="text-center px-6 max-w-2xl">
        <p lang="ar" className="text-4xl sm:text-5xl text-rose-200/80">
          عقيقة
        </p>
        <h2
          className={`font-royal text-3xl sm:text-5xl gold-text mt-5 leading-tight ${ur ? "font-urdu leading-[1.9]" : ""}`}
          dir={ur ? "rtl" : "ltr"}
        >
          {ur ? "عقیقہ مبارک، ننھی ہایا" : "Aqiqah Mubarak, Little Haya"}
        </h2>
        <p className={`mt-6 text-2xl sm:text-3xl text-rose-200/90 ${ur ? "font-urdu" : "font-script"}`} dir={ur ? "rtl" : "ltr"}>
          {ur ? "کہانی جاری ہے — اور آپ اس میں شامل ہیں" : "the story continues — with you in it"}
        </p>
        <div className="mt-8 glass-card rounded-full inline-block px-6 py-2.5 text-[0.68rem] sm:text-xs tracking-[0.3em] uppercase text-amber-100/90">
          {CEREMONY_DATE_LABEL} · {VENUE_NAME}
        </div>
        <div className="mt-9 flex flex-wrap justify-center gap-4">
          <a data-testid="journey-rsvp-btn" href={whatsappUrl()} target="_blank" rel="noopener noreferrer" className="btn-gold">
            <MessageCircle size={15} /> {ur ? "واٹس ایپ پر RSVP" : "RSVP on WhatsApp"}
          </a>
          <a data-testid="journey-back-link" href="/" className="btn-ghost">
            {ur ? "دعوت نامے پر واپس جائیں" : "Return to the Invitation"}
          </a>
        </div>
      </div>
    </SceneFrame>
  );
};
